import { api } from "./client";
import type { HeldPositionRouting } from "./commissions";
import type { PagedQuery, PagedResult, UserSummaryDto } from "@/types/api";

export interface CreateUserPayload {
  email: string;
  displayName: string;
  jobTitle?: string;
  department?: string;
  roleCodes: string[];
  primaryRoleCode?: string;
  /** Optional. If provided, validated against the Identity password policy
   * and applied to the new user; if validation fails the entire create rolls
   * back. Leave empty for the existing temp-password flow. */
  initialPassword?: string;
}

export interface UpdateUserPayload {
  displayName: string;
  jobTitle?: string | null;
  department?: string | null;
  /** Omit (or send null) to leave the account's active flag UNCHANGED.
   *
   *  It used to be required here and a non-nullable `bool` on the server,
   *  assigned unconditionally — so a payload that omitted it deactivated the
   *  user, which locks them out of sign-in and drops every notification email
   *  addressed to them. The edit modal always sends it; this optionality is
   *  the honest mirror of the server contract, not an invitation to omit it. */
  isActive?: boolean | null;
  /** Admins can only CLEAR an avatar (security) — the URL itself is
   *  user-owned via the profile photo upload. */
  clearAvatar?: boolean;
  /**
   * U11 — the last day this person's F1/F2 book shares are paid, INCLUSIVE.
   * A bare `YYYY-MM-DD`, which is what an `<input type="date">` already emits
   * and what a `DateOnly?` binds losslessly. Never an ISO instant: widening it
   * to UTC midnight ends the arrangement seven hours early.
   *
   * ⚠️ THIS PUT IS A FULL REPLACE. Omitting the field, or sending null,
   * CLEARS the date — the same as `jobTitle` and `department` beside it. Hydrate
   * it from the user before opening the form.
   */
  commissionEndDate?: string | null;
}

export interface AssignRolesPayload {
  roleCodes: string[];
  primaryRoleCode?: string;
}

export interface AssignableUserDto {
  id: string;
  displayName: string;
  email: string;
  roleCodes: string[];
}

export const usersApi = {
  list: (query?: PagedQuery) => api.get<PagedResult<UserSummaryDto>>("/users", query),
  get: (id: string) => api.get<UserSummaryDto>(`/users/${id}`),
  invite: (body: CreateUserPayload) => api.post<UserSummaryDto>("/users", body),
  update: (id: string, body: UpdateUserPayload) => api.put<UserSummaryDto>(`/users/${id}`, body),
  assignRoles: (id: string, body: AssignRolesPayload) => api.put<UserSummaryDto>(`/users/${id}/roles`, body),
  deactivate: (id: string) => api.delete<void>(`/users/${id}`),
  setPassword: (id: string, newPassword: string) =>
    api.post<void>(`/users/${id}/set-password`, { newPassword }),
  /** Branded welcome email with a single-use set-your-own-password link. */
  sendWelcome: (id: string) => api.post<void>(`/users/${id}/send-welcome`),
  // Ownership/Assignment §3 — `roles` is a CSV (e.g. "C5,C0,Admin").
  assignable: (roles?: string) =>
    api.get<AssignableUserDto[]>("/users/assignable", roles ? { roles } : undefined),
};

interface RoleCodeDto {
  code: string;
  title: string;
}
interface RoleMatrixDto {
  roles: RoleCodeDto[];
  permissions: string[];
  grants: Record<string, string[]>;
}

/**
 * Roles &amp; Permissions admin matrix. Powers the
 * <c>/admin/roles-permissions</c> page. The backing endpoint moved from
 * the canonical <c>/api/roles</c> to <c>/api/roles/matrix</c> as part of
 * the Admin-managed Lookups PR 2 reclaim — the canonical route is now the
 * user-facing active-roles lookup that drives dropdowns across the app
 * (see <c>useRoles</c> in <c>@/hooks/useRoles</c>).
 */
export const rolesApi = {
  matrix: () => api.get<RoleMatrixDto>("/roles/matrix"),
  get: (roleCode: string) => api.get<string[]>(`/roles/${roleCode}/permissions`),
  set: (roleCode: string, permissions: string[]) =>
    api.put<void>(`/roles/${roleCode}/permissions`, { permissions }),
};

/**
 * What granting one role code will do to HELD commission roll-ups.
 *
 * 🔴 `effect` DISTINGUISHES TWO OPPOSITE OUTCOMES and must not be collapsed to
 * a boolean. A firm-wide position resolves only at EXACTLY ONE holder, so a
 * grant releases held money at zero holders and FREEZES it at one — and the
 * second is the case nothing surfaces today, because a position that stops
 * resolving simply stops producing claims and nobody is told.
 *
 * Mirrors `RoleGrantPreflightDto` / `RoleGrantHeldEffect`.
 */
export type RoleGrantHeldEffect =
  | "None"
  | "RoutesHeldRollups"
  | "FreezesFutureRollups"
  | "RoutedByBook";

export interface RoleGrantPreflightDto {
  roleCode: string;
  /** 🔴 THE CANONICAL UNION, IMPORTED RATHER THAN RESTATED. This field used to
   *  carry its own inline copy of the three tiers, and it had already drifted:
   *  `Book` arrived with the Buyer Manager / Buyer Consultant work and was never
   *  added here, so the one type that describes what this surface is about was
   *  silently missing the tier it most needed to name. A second definition of a
   *  server enum is a second thing to remember. */
  routing: HeldPositionRouting;
  /** Distinct holders BEFORE this grant.
   *
   *  ⚠️ ZERO on every tier but `FirmWide`, because only that tier resolves on a
   *  count and the server deliberately does not run the query otherwise. It is
   *  NOT an assertion that nobody holds the code — render no holder sentence
   *  off it outside the firm-wide arms. */
  currentHolderCount: number;
  effect: RoleGrantHeldEffect;
  /** Zero and unqueried on every tier a grant does not route. */
  heldRollupCount: number;
  /** APPROXIMATE — the sweep redrafts rather than reassigns, and re-prices
   *  against any per-user rate override the new holder carries. Copy hedges it. */
  heldRollupTotal: number;
  lookbackMonths: number;
}

/**
 * Read-only disclosure asked before a role grant is saved. Gated server-side on
 * the same `Users.Update` permission as the grant itself; it refuses nothing.
 */
export const roleGrantPreflightApi = {
  describe: (roleCode: string) =>
    api.get<RoleGrantPreflightDto>("/users/role-grant-preflight", { roleCode }),
};
